// tienda/ReferidosSection.jsx

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, ChevronRight, Gift, HandCoins, Share2, UserPlus } from "lucide-react";
import { brand, fadeUp, WA_HREF } from "../shared/brand";

const SOL_PER_DELIVERY = 1;
const MONTHS_DURATION = 4;

const STEPS = [
  { icon: <Share2 className="size-4" />, title: "Comparte tu código", text: "Tu código único vive en tu perfil de Fast Max." },
  { icon: <UserPlus className="size-4" />, title: "Tu referido se registra", text: "La tienda ingresa tu código al crear su cuenta." },
  { icon: <HandCoins className="size-4" />, title: "Ganas por entrega", text: "Recibes S/ 1 por cada entrega durante 4 meses." },
];

export function ReferidosSection() {
  return (
    <section id="referidos" className="border-b bg-white scroll-mt-16" style={{ borderColor: brand.line }}>
      <div className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <motion.div {...fadeUp} className="mb-10 grid gap-5 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-black text-white" style={{ background: brand.accentGold }}>
              <Gift className="size-3.5" />
              Programa de referidos
            </div>
            <h2 className="text-3xl font-black md:text-4xl" style={{ color: brand.ink }}>
              Refiere tiendas y gana mientras ellas entregan.
            </h2>
          </div>
          <p className="text-sm leading-relaxed md:text-base" style={{ color: brand.muted }}>
            Ganas <strong>S/ 1 por cada entrega</strong> que haga cada tienda que traigas
            a Fast Max durante sus primeros <strong>4 meses</strong>.
          </p>
        </motion.div>

        <div className="grid gap-5 lg:grid-cols-2">
          <HowItWorksCard />
          <EarningsCalculator />
        </div>

        <motion.div {...fadeUp} className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-black text-white"
            style={{ background: brand.primary }}
          >
            Crear cuenta y obtener mi código
            <ChevronRight className="size-4" />
          </Link>
          <a href={WA_HREF} target="_blank" rel="noopener noreferrer" className="text-sm font-black hover:underline" style={{ color: brand.primary }}>
            Tengo preguntas sobre el programa
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorksCard() {
  return (
    <motion.div {...fadeUp} className="rounded-2xl border p-6 shadow-sm" style={{ borderColor: brand.line, background: brand.bg }}>
      <h3 className="mb-6 text-lg font-black" style={{ color: brand.ink }}>Cómo funciona</h3>
      <div className="space-y-4">
        {STEPS.map((step, index) => (
          <div key={step.title} className="flex gap-4 rounded-2xl border bg-white p-4" style={{ borderColor: brand.line }}>
            <span className="grid size-9 shrink-0 place-items-center rounded-xl text-white" style={{ background: index === 2 ? brand.accentGreen : brand.primary }}>
              {step.icon}
            </span>
            <div>
              <p className="text-sm font-black" style={{ color: brand.ink }}>{step.title}</p>
              <p className="mt-1 text-xs leading-relaxed" style={{ color: brand.muted }}>{step.text}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-dashed bg-white px-4 py-3" style={{ borderColor: brand.line }}>
        <p className="text-[11px] font-black uppercase tracking-[0.18em]" style={{ color: brand.muted }}>
          Código de ejemplo
        </p>
        <p className="mt-2 text-xl font-black tracking-[0.2em]" style={{ color: brand.primary }}>
          TIENDA-4829
        </p>
      </div>
    </motion.div>
  );
}

function EarningsCalculator() {
  const [referees, setReferees] = useState(3);
  const [deliveries, setDeliveries] = useState(20);

  const perDay = referees * deliveries * SOL_PER_DELIVERY;
  const perMonth = perDay * 30;
  const total = perMonth * MONTHS_DURATION;

  return (
    <motion.div {...fadeUp} className="rounded-2xl border bg-white p-6 shadow-sm" style={{ borderColor: brand.line }}>
      <div className="mb-6 flex items-center gap-2">
        <span className="grid size-10 place-items-center rounded-xl" style={{ background: `${brand.primary}12`, color: brand.primary }}>
          <Calculator className="size-5" />
        </span>
        <h3 className="text-lg font-black" style={{ color: brand.ink }}>Calculadora de ganancias</h3>
      </div>

      <div className="space-y-5">
        <SliderField label="Referidos activos" value={referees} min={1} max={20} onChange={setReferees} display={`${referees} tienda${referees !== 1 ? "s" : ""}`} />
        <SliderField label="Entregas por tienda / día" value={deliveries} min={1} max={100} onChange={setDeliveries} display={`${deliveries} entregas`} />
      </div>

      <div className="mt-7 grid grid-cols-3 gap-2">
        <ResultPill label="Por día" value={`S/ ${perDay.toLocaleString()}`} color={brand.accentGold} />
        <ResultPill label="Por mes" value={`S/ ${perMonth.toLocaleString()}`} color={brand.accentGreen} />
        <ResultPill label="4 meses" value={`S/ ${total.toLocaleString()}`} color={brand.primary} />
      </div>
    </motion.div>
  );
}

function SliderField({ label, value, min, max, onChange, display }) {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-black" style={{ color: brand.muted }}>{label}</label>
        <span className="text-xs font-black" style={{ color: brand.primary }}>{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full cursor-pointer appearance-none rounded-full"
        style={{ height: 6, background: `linear-gradient(to right, ${brand.primary} ${pct}%, ${brand.line} ${pct}%)` }}
      />
    </div>
  );
}

function ResultPill({ label, value, color }) {
  return (
    <div className="rounded-2xl p-3 text-center" style={{ background: `${color}12` }}>
      <p className="text-[10px] font-black" style={{ color: brand.muted }}>{label}</p>
      <p className="mt-1 text-sm font-black" style={{ color }}>{value}</p>
    </div>
  );
}
